import { useEffect, useMemo, useState } from 'react';
import type { Hash } from 'viem';
import { parseEther } from 'viem';
import { useBalance } from 'wagmi';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { getTransactionReceipt } from '@context/transaction/TransactionStatusStore';
import { CollectSoundSwapEthConversionDocument, useQuery } from '@graphql/reactQuery';
import { getDepositEventsInfoFromTxReceipt, getL2HashFromL1DepositInfo } from '@utils/bridge';
import { chainFromId } from '@utils/chain';
import { AVAILABLE_CHAIN_IDS, getOpL1StandardBridgeAddress } from '@utils/constants';
import { etherscanForTransaction, formatWeiToEthForDisplay } from '@utils/helpers';

const opL1StandardBridgeAbi = [
  {
    inputs: [
      { internalType: 'uint32', name: '_minGasLimit', type: 'uint32' },
      { internalType: 'bytes', name: '_extraData', type: 'bytes' },
    ],
    name: 'depositETH',
    outputs: [],
    stateMutability: 'payable',
    type: 'function',
  },
  ,
] as const;

const TOAST_DURATION = 1000 * 60 * 5;

export const useBridge = ({
  amountInEth,
  onBridge,
}: {
  amountInEth: string;
  onBridge: () => void;
}) => {
  const amountInWei = useMemo(() => parseEther(amountInEth), [amountInEth]);
  const { openToast } = useToast();
  const { authUserAddress } = useAuth();
  const { getSigner, expectMatchingChain, provider, isSwitchingNetwork } = useConnectedAuth();
  const [isBridgeStarted, setIsBridgeStarted] = useState(false);
  const l1ChainId = AVAILABLE_CHAIN_IDS.eth;
  const l2ChainId = AVAILABLE_CHAIN_IDS.optimism;
  const { data: ethToUsdData } = useQuery(CollectSoundSwapEthConversionDocument, {
    filterQueryKey: {
      key: 'EthToUsd',
    },
    staleTime: 0,
  });
  const ethToUsdValue = ethToUsdData?.data.currencies.ethToUsd ?? 0;

  const {
    data: ethBalance,
    isLoading: isEthLoading,
    isError: isEthBalanceError,
  } = useBalance({
    address: authUserAddress ?? '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'wei',
    chainId: l1ChainId,
  });

  const ethBalanceInEth = useMemo(
    () =>
      ethBalance?.value
        ? formatWeiToEthForDisplay({
            weiValue: ethBalance?.value.toString(),
            maxDecimals: 4,
          })
        : '0',
    [ethBalance?.value],
  );

  const usdEthBalance = useMemo(
    () => (ethToUsdValue ? (ethToUsdValue * Number(ethBalanceInEth)).toFixed(2) : undefined),
    [ethBalanceInEth, ethToUsdValue],
  );

  const {
    data: ethOptimismBalance,
    isLoading: isOptimismLoading,
    isError: isOptimismBalanceError,
  } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'wei',
    chainId: AVAILABLE_CHAIN_IDS.optimism,
  });

  const ethOptimismBalanceInEth = useMemo(
    () =>
      ethOptimismBalance?.value
        ? formatWeiToEthForDisplay({
            weiValue: ethOptimismBalance?.value.toString(),
            maxDecimals: 4,
          })
        : '0',
    [ethOptimismBalance?.value],
  );

  const usdEthOptimismBalance = useMemo(
    () =>
      ethToUsdValue ? (ethToUsdValue * Number(ethOptimismBalanceInEth)).toFixed(2) : undefined,
    [ethOptimismBalanceInEth, ethToUsdValue],
  );

  const isAmountMoreThanZero = useMemo(() => {
    return amountInWei > BigInt(0);
  }, [amountInWei]);

  const isSufficientFunds = useMemo(() => {
    return amountInWei <= (ethBalance?.value ?? BigInt(0));
  }, [amountInWei, ethBalance?.value]);

  const isBalanceLoading = isEthLoading || isOptimismLoading;
  const isBalanceError = useMemo(
    () => isEthBalanceError || isOptimismBalanceError,
    [isEthBalanceError, isOptimismBalanceError],
  );
  const isLoading = isSwitchingNetwork || isBridgeStarted;

  const disabled =
    !isAmountMoreThanZero ||
    !isSufficientFunds ||
    isBalanceLoading ||
    isBalanceError ||
    isBridgeStarted;

  useEffect(() => {
    if (isBalanceError) {
      openToast({
        text: 'Unable to check your balance. Please try again later.',
        variant: 'error',
      });
    }
  }, [isBalanceError, openToast]);

  const state = useMemo(() => {
    if (isSwitchingNetwork) {
      return 'Switch chain in wallet';
    }
    if (!isSufficientFunds) {
      return 'Insufficient funds';
    }
    if (isBridgeStarted) {
      return 'Confirm in wallet';
    }
    return 'Bridge';
  }, [isBridgeStarted, isSufficientFunds, isSwitchingNetwork]);

  const waitForTransaction = async ({
    transactionHash,
    amountInEth,
  }: {
    transactionHash: Hash;
    amountInEth: string;
  }) => {
    const chainProvider = provider(l1ChainId);
    // lets wait for more than 1 confirmation for safety
    const receipt = await chainProvider.waitForTransactionReceipt({
      hash: transactionHash,
      confirmations: 2,
    });

    const depositEvents = getDepositEventsInfoFromTxReceipt({ receipt });
    const depositEvent = depositEvents[0];

    if (!depositEvent) {
      return openToast({
        text: 'Unable to check the transfer. Please check your balance in a few minutes.',
        variant: 'error',
      });
    }

    const { event, logIndex } = depositEvent;

    const l2ChainProvider = provider(l2ChainId);

    const l2TransactionHash = getL2HashFromL1DepositInfo({
      event,
      logIndex,
      blockHash: receipt.blockHash,
    });

    const l2TransactionReceipt = await getTransactionReceipt({
      chainId: l2ChainId,
      hash: l2TransactionHash,
      provider: l2ChainProvider,
      /*
       * L2 transaction hash is computed from the L1 transaction, but there may be a delay before
       * the Optimism bridge executes the L2 side. To account for that, we are polling at 20s
       */
      interval: 20_000,
    });

    if (l2TransactionReceipt.status === 'success') {
      openToast({
        text: `Bridging complete! ${amountInEth} ETH was added to Optimism.`,
        variant: 'success',
        ctaText: 'View on Etherscan',
        ctaHref: etherscanForTransaction({
          transactionHash: l2TransactionHash,
          chainId: l2ChainId,
        }),
        duration: TOAST_DURATION,
      });
    }
  };

  const bridge = useStableCallback(async () => {
    setIsBridgeStarted(true);
    const chain = expectMatchingChain(l1ChainId, {
      onSyncSwitchSuccess() {
        openToast({
          text: `Successfully switched to Ethereum.\nTap Bridge to continue.`,
          variant: 'success',
        });
      },
    });
    if (chain.stop) {
      return setIsBridgeStarted(false);
    }
    try {
      if (chain.switching) await chain.switching;
    } catch (error) {
      setIsBridgeStarted(false);
      openToast({
        text: 'Please switch to ethereum mainnet',
        variant: 'error',
      });
      return;
    }

    const signer = await getSigner(l1ChainId);

    try {
      const transactionHash = await signer.writeContract({
        address: getOpL1StandardBridgeAddress(l1ChainId),
        abi: opL1StandardBridgeAbi,
        functionName: 'depositETH',
        // L2 gas fees defaulted to 200k, based on what optimism sdk does, excess gas is refunded, so doesn't matter much
        // https://github.com/ethereum-optimism/optimism/blob/7b30659f5372ba7ec3981deb3f3d3d95b8c08a29/packages/sdk/src/adapters/eth-bridge.ts#L142
        args: [200_000, '0x'],
        value: amountInWei,
        chain: chainFromId(l1ChainId),
      });

      openToast({
        text: `Your Optimism ETH is on the way and may take a few minutes.`,
        variant: 'pending',
        ctaText: 'View on Etherscan',
        ctaHref: etherscanForTransaction({
          transactionHash,
          chainId: l1ChainId,
        }),
        duration: TOAST_DURATION,
      });

      onBridge();

      await waitForTransaction({ transactionHash, amountInEth });
    } catch (error) {
      openToast({ text: 'Error during bridge transaction.', variant: 'error' });
    } finally {
      setIsBridgeStarted(false);
    }
  });

  return {
    bridge,
    ethBalanceInEth,
    usdEthBalance,
    ethOptimismBalanceInEth,
    usdEthOptimismBalance,
    isBalanceLoading,
    isBalanceError,
    isLoading,
    disabled,
    state,
  };
};
